import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { canUseMultiEstablishmentContext, useAuthStore } from '@/stores/auth';
import { useAuthorizationStore } from '@/stores/authorization';
import { useCustomReportNavigation } from '@/hooks/useCustomReportNavigation';

interface FlatRoute {
  key: string;
  path: string;
  label: string;
  parentLabel?: string;
  moduleKey: string;
  moduleLabel: string;
  menuOrder: number;
  parentOrder: number;
}

interface RouteSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const MODULE_LABEL_KEYS: Record<string, string> = {
  administrative: 'module_selector.modules.administrative',
  establishment: 'module_selector.modules.establishment',
  medical_company: 'module_selector.modules.medical_company',
  medical_management: 'module_selector.modules.medical_management',
};

const MODULES_REQUIRING_ESTABLISHMENT_CONTEXT = [
  'establishment',
  'medical_company',
];
const MEDICAL_COMPANY_MODULE_KEY = 'medical_company';
const MEDICAL_COMPANY_HOME_KEY = 'company_home';

export function RouteSearchDialog({ open, onClose }: RouteSearchDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const payload = useAuthorizationStore((s) => s.payload);
  const customReports = useCustomReportNavigation();
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const setSelectedModule = useAuthorizationStore((s) => s.setSelectedModule);
  const user = useAuthStore((s) => s.user);
  const companyConfigId = user?.company_config_id;
  const requireContextSelection = useAuthStore(
    (s) => s.requireContextSelection,
  );
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const getModuleLabel = useCallback(
    (moduleKey: string) => {
      const translationKey = MODULE_LABEL_KEYS[moduleKey];
      return translationKey ? t(translationKey) : moduleKey;
    },
    [t],
  );

  const flatRoutes = useMemo<FlatRoute[]>(() => {
    if (!payload) return [];

    const moduleKeys = new Set(payload.modules.map((module) => module.key));
    const allowedRoutes = new Set([...payload.routes, ...customReports.map((report) => report.route!)]);
    const functionalitiesByKey = new Map(
      payload.functionalities.map((functionality) => [
        functionality.key,
        functionality,
      ]),
    );
    const dedupedRoutes = new Map<string, FlatRoute>();

    for (const functionality of [...payload.functionalities, ...customReports]) {
      if (
        functionality.module_key === MEDICAL_COMPANY_MODULE_KEY &&
        !companyConfigId &&
        functionality.key !== MEDICAL_COMPANY_HOME_KEY
      ) {
        continue;
      }

      if (!functionality.route || !allowedRoutes.has(functionality.route)) {
        continue;
      }
      if (!moduleKeys.has(functionality.module_key)) {
        continue;
      }

      const parent = functionality.parent_key
        ? functionalitiesByKey.get(functionality.parent_key)
        : undefined;

      const route: FlatRoute = {
        key: functionality.key,
        path: functionality.route,
        label: functionality.name,
        parentLabel: parent?.name,
        moduleKey: functionality.module_key,
        moduleLabel: getModuleLabel(functionality.module_key),
        menuOrder: functionality.menu_order,
        parentOrder: parent?.menu_order ?? functionality.menu_order,
      };

      const previous = dedupedRoutes.get(route.path);
      if (!previous || route.menuOrder < previous.menuOrder) {
        dedupedRoutes.set(route.path, route);
      }
    }

    return Array.from(dedupedRoutes.values()).sort((a, b) => {
      if (a.moduleKey === selectedModuleKey && b.moduleKey !== selectedModuleKey) {
        return -1;
      }
      if (a.moduleKey !== selectedModuleKey && b.moduleKey === selectedModuleKey) {
        return 1;
      }
      if (a.moduleLabel !== b.moduleLabel) {
        return a.moduleLabel.localeCompare(b.moduleLabel);
      }
      if (a.parentOrder !== b.parentOrder) {
        return a.parentOrder - b.parentOrder;
      }
      return a.menuOrder - b.menuOrder;
    });
  }, [companyConfigId, getModuleLabel, payload, selectedModuleKey, customReports]);

  const filteredRoutes = useMemo<FlatRoute[]>(() => {
    if (!query.trim()) return flatRoutes;
    const q = query.toLowerCase().trim();
    return flatRoutes.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        (r.parentLabel && r.parentLabel.toLowerCase().includes(q)) ||
        r.moduleLabel.toLowerCase().includes(q) ||
        r.path.toLowerCase().includes(q),
    );
  }, [flatRoutes, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = useCallback(
    (route: FlatRoute) => {
      const isSwitchingModule = route.moduleKey !== selectedModuleKey;

      if (isSwitchingModule) {
        setSelectedModule(route.moduleKey);
      }

      const requiresEstablishmentContext =
        MODULES_REQUIRING_ESTABLISHMENT_CONTEXT.includes(route.moduleKey);
      if (
        isSwitchingModule &&
        requiresEstablishmentContext &&
        canUseMultiEstablishmentContext(user)
      ) {
        requireContextSelection(route.path);
        onClose();
        return;
      }

      navigate(route.path);
      onClose();
    },
    [
      navigate,
      onClose,
      requireContextSelection,
      selectedModuleKey,
      setSelectedModule,
      user,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredRoutes.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredRoutes[selectedIndex]) {
          handleSelect(filteredRoutes[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [filteredRoutes, selectedIndex, handleSelect, onClose],
  );

  useEffect(() => {
    if (!listRef.current) return;
    const selectedEl = listRef.current.querySelector<HTMLElement>(
      '[data-selected="true"]',
    );
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />

          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4 pointer-events-none">
            <motion.div
              className="pointer-events-auto w-full max-w-[600px] rounded-xl overflow-hidden shadow-2xl border border-border bg-card text-card-foreground"
              initial={{ opacity: 0, scale: 0.96, y: -12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -12 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <svg
                  className="w-4 h-4 text-card-foreground/40 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('search.placeholder', {
                    defaultValue: 'Pesquisar rotas...',
                  })}
                  className="flex-1 bg-transparent text-sm text-card-foreground placeholder:text-card-foreground/40 outline-none"
                  autoComplete="off"
                  spellCheck={false}
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono text-card-foreground/50 bg-card-foreground/8 border border-border">
                  Esc
                </kbd>
              </div>

              <div
                ref={listRef}
                className="max-h-[380px] overflow-y-auto overscroll-contain py-2"
              >
                {filteredRoutes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center text-card-foreground/50">
                    <svg
                      className="w-8 h-8 mb-2 opacity-40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm">
                      {t('common.no_results', {
                        defaultValue: 'Nenhum resultado encontrado',
                      })}
                    </p>
                  </div>
                ) : (
                  filteredRoutes.map((route, index) => {
                    const isSelected = index === selectedIndex;

                    return (
                      <motion.button
                        key={`${route.key}-${route.path}`}
                        data-selected={isSelected}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-primary/10 text-card-foreground'
                            : 'text-card-foreground hover:bg-card-foreground/5'
                        }`}
                        onClick={() => handleSelect(route)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.12, delay: index * 0.02 }}
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate text-card-foreground">
                            {route.label}
                          </span>
                          <span className="text-xs text-card-foreground/50 truncate">
                            {[route.moduleLabel, route.parentLabel]
                              .filter(Boolean)
                              .join(' / ')}
                          </span>
                          {route.moduleKey !== selectedModuleKey && (
                            <span className="text-xs text-card-foreground/50 truncate">
                              {t('search.switch_module', {
                                defaultValue: 'Trocar para este módulo',
                              })}
                            </span>
                          )}
                        </div>

                        <span className="ml-auto text-[11px] text-card-foreground/40 font-mono truncate max-w-[140px] hidden sm:block">
                          {route.path}
                        </span>

                        {isSelected && (
                          <motion.kbd
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-card-foreground/50 bg-card-foreground/8 border border-border shrink-0"
                          >
                            ↵
                          </motion.kbd>
                        )}
                      </motion.button>
                    );
                  })
                )}
              </div>

              <div className="flex items-center gap-4 px-4 py-2 border-t border-border text-[11px] text-card-foreground/50">
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex items-center px-1 py-0.5 rounded bg-card-foreground/8 border border-border font-mono">
                    ↑↓
                  </kbd>
                  {t('search.navigate', { defaultValue: 'navegar' })}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex items-center px-1 py-0.5 rounded bg-card-foreground/8 border border-border font-mono">
                    ↵
                  </kbd>
                  {t('search.select', { defaultValue: 'selecionar' })}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex items-center px-1 py-0.5 rounded bg-card-foreground/8 border border-border font-mono">
                    Esc
                  </kbd>
                  {t('search.close', { defaultValue: 'fechar' })}
                </span>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
